var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, useEffect } from 'react';
import InfoCardGroupBase from '@widget/list/InfoCardGroup/InfoCardGroupBase';
import Icon from '@icon/Icon';
import Button from '@widget/buttons/Button';
import { useUXPContext } from '@core/context/useUXPContext';
import useAlert from '@notifications/Alerts/useAlerts';
import MultiSelect from './MultiSelect';
/**
 * @export
 * A form input component for selecting multiple items with InfoCard-style presentation.
 * Similar to MultiSelect but displays selected items as stacked profile images.
 *
 * @example Basic usage with static options:
 * ```
 * const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
 *
 * <InfoCardGroupInput
 *   options={[
 *     { name: 'Team A', id: 'team-a' },
 *     { name: 'Team B', id: 'team-b' }
 *   ]}
 *   selected={selectedTeams}
 *   onChange={(values) => setSelectedTeams(values)}
 *   labelField="name"
 *   valueField="id"
 * />
 * ```
 *
 * @example With custom field mappings:
 * ```
 * <InfoCardGroupInput
 *   options={users}
 *   selected={selectedUserIds}
 *   onChange={setSelectedUserIds}
 *   labelField="fullName"
 *   valueField="userId"
 *   fields={{
 *     image: 'avatar',
 *     name: 'fullName',
 *     title: 'fullName',
 *     subtitle: 'email'
 *   }}
 * />
 * ```
 *
 * @example In DynamicForm:
 * ```
 * {
 *   name: 'teamMembers',
 *   label: 'Team Members',
 *   type: 'json',
 *   renderField: (data, onValueChange) => (
 *     <InfoCardGroupInput
 *       options={availableUsers}
 *       selected={data.teamMembers || []}
 *       onChange={onValueChange}
 *       labelField="name"
 *       valueField="id"
 *     />
 *   )
 * }
 * ```
 */
const InfoCardGroupInput = (props) => {
    const context = useUXPContext();
    const alerts = useAlert();
    const { options, selected = [], onChange, labelField = 'label', valueField = 'value', fields, placeholder, className, isValid, maxVisible = 5, size = 'medium', shape = 'circle', dropdownPosition = 'bottom-left', addButtonLabel, pageSize, selectedLabels } = props;
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const isDynamicLoading = typeof options === 'function';
    // Build default fields mapping if not provided
    const effectiveFields = useMemo(() => {
        if (fields)
            return fields;
        // Default: use labelField for name/title
        return {
            name: labelField,
            title: labelField,
            subtitle: undefined,
            image: undefined
        };
    }, [fields, labelField]);
    // Update selected options when selected values or options change
    useEffect(() => {
        updateSelectedOptions();
    }, [selected, options, selectedLabels]);
    function updateSelectedOptions() {
        return __awaiter(this, void 0, void 0, function* () {
            setLoadingOptions(true);
            let selOpts = [];
            if (selected && selected.length > 0) {
                if (Array.isArray(options)) {
                    selOpts = options.filter((option) => (!!option && selected.indexOf(option[valueField]) !== -1));
                }
                else if (!!selectedLabels) {
                    if (Array.isArray(selectedLabels)) {
                        selOpts = selected.map((val, index) => ({
                            [labelField]: selectedLabels[index] || val,
                            [valueField]: val
                        }));
                    }
                    else if (typeof selectedLabels === 'function') {
                        try {
                            const opts = yield selectedLabels(selected);
                            if (Array.isArray(opts) && opts.length > 0) {
                                selOpts = opts;
                            }
                        }
                        catch (e) {
                            console.error('Unable to get the selected options details. error:', e);
                        }
                    }
                }
            }
            setSelectedOptions(selOpts);
            setLoadingOptions(false);
        });
    }
    // Handle removing an item
    const handleRemoveItem = (item) => {
        const itemValue = item[valueField];
        const newValues = selected.filter(v => v !== itemValue);
        const newOptions = selectedOptions.filter(opt => opt[valueField] !== itemValue);
        onChange(newValues, newOptions);
    };
    // Handle adding new items via alert form
    const handleAddItems = () => __awaiter(void 0, void 0, void 0, function* () {
        const result = yield alerts.form({
            title: addButtonLabel || context.$L('uxp-core.text.add'),
            formStructure: [
                {
                    title: '',
                    columns: 1,
                    fields: [
                        {
                            name: 'items',
                            label: context.$L('uxp-core.text.select-items'),
                            type: 'json',
                            value: selected,
                            renderField: (data, onValueChange) => {
                                return (_jsx(MultiSelect, { selected: (data === null || data === void 0 ? void 0 : data.items) || [], onChange: (values) => onValueChange(values), options: options, labelField: labelField, valueField: valueField, placeholder: placeholder || context.$L('uxp-core.text.select-items'), pageSize: pageSize, selectedLabels: selectedLabels }));
                            }
                        }
                    ]
                }
            ]
        });
        if (result && result.items) {
            // Get full option objects for the selected values
            let newOptions = [];
            if (Array.isArray(options)) {
                newOptions = options.filter((opt) => result.items.includes(opt[valueField]));
            }
            // For dynamic functions (paginated data), newOptions will be empty
            // The selectedLabels prop will handle loading the details
            onChange(result.items, newOptions.length > 0 ? newOptions : undefined);
        }
    });
    // Render dropdown item with remove button
    const renderDropdownItem = (item, index, defaultContent) => {
        return (_jsxs("div", { className: "uxpcore_infocardgroup__item-wrapper", children: [defaultContent, _jsx("div", { className: "uxpcore_infocardgroup__remove-btn", onClick: (e) => {
                        e.stopPropagation();
                        handleRemoveItem(item);
                    }, title: context.$L('uxp-core.text.remove'), children: _jsx(Icon, { icon: "fas times" }) })] }));
    };
    // Render footer with Add button
    const renderFooter = () => {
        return (_jsx(Button, { title: addButtonLabel || context.$L('uxp-core.text.add'), onClick: handleAddItems, className: "uxpcore_infocardgroup__add-btn", variant: 'secondary', leftIcon: 'fas plus' }));
    };
    if (loadingOptions) {
        return (_jsx("div", { className: "uxpcore_infocardgroup uxpcore_infocardgroup--loading", children: _jsx(Icon, { icon: "fas spinner", spin: true }) }));
    }
    return (_jsx(InfoCardGroupBase, { items: selectedOptions, fields: effectiveFields, maxVisible: maxVisible, size: size, shape: shape, dropdownPosition: dropdownPosition, className: className, renderDropdownItem: renderDropdownItem, dropdownFooter: renderFooter(), enableAddButton: true }));
};
export default InfoCardGroupInput;
